import React, { useState, useEffect, useRef } from 'react';
import AppSpinner from "../Spinner/AppSpinner";
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { BASE_URL } from '../../constants';
import { apiFetch } from "../../utils/apiFetch";
import { useApiError } from '../../utils/useApiError';
import ValidationErrorModal from '../ValidationErrorModal/ValidationErrorModal';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileSelectInput = ({ selectedFile, onSelect }) => {
  const inputRef = useRef();
  return (
    <div>
      <div
        onClick={() => inputRef.current.click()}
        style={{
          border: `2px dashed ${selectedFile ? '#22c55e' : '#d1d5db'}`,
          borderRadius: 6, padding: '8px 10px',
          background: selectedFile ? '#f0fdf4' : '#fafafa',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          transition: 'all 0.15s'
        }}
      >
        {selectedFile ? (
          <>
            <svg viewBox="0 0 20 20" fill="none" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <path d="M4 10l4 4 8-8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: 11, color: '#15803d', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedFile.name} {selectedFile.size ? `(${formatFileSize(selectedFile.size)})` : ''}
            </span>
            <span
              onClick={e => { e.stopPropagation(); onSelect(null); }}
              style={{ fontSize: 10, color: '#ef4444', cursor: 'pointer', flexShrink: 0, fontWeight: 700 }}
            >
              ✕
            </span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 20 20" fill="none" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <path d="M10 3v10M6 7l4-4 4 4M4 15h12" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Fayl seçmək üçün klikləyin</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={e => { onSelect(e.target.files[0] || null); e.target.value = ''; }}
      />
    </div>
  );
};

const LectureWorks = ({ lectureId, initialWorks = [], onRefresh, onShowToast }) => {
  const [works, setWorks] = useState(initialWorks);
  const [selectedWork, setSelectedWork] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', sector: '' });
  const [addFile, setAddFile] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const { errorModal, clearError, handleApiError } = useApiError();

  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', sector: '', fileUuidName: '' });
  const [editFile, setEditFile] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    setWorks(initialWorks);
    setSelectedWork(null);
  }, [initialWorks]);

  // fileUuidName və fileName hər ikisini qaytarır
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiFetch(`${BASE_URL}/files/upload-lecture-task`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) throw new Error('Fayl yüklənərkən xəta baş verdi.');
    const data = await response.json();
    return {
      fileUuidName: data.fileUuidName || data.fileEntity?.fileUuidName || '',
      fileName: data.fileName || file.name || ''
    };
  };

  // DELETE
  const handleDelete = async () => {
    try {
      const res = await apiFetch(`${BASE_URL}/lecture-tasks/${selectedWork.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        let msg = 'Silinərkən xəta baş verdi.';
        try { const d = await res.json(); msg = d.message || d.error || msg; } catch (_) {}
        throw new Error(msg);
      }
      setWorks(prev => prev.filter(w => w.id !== selectedWork.id));
      setSelectedWork(null);
      setShowDeleteConfirm(false);
      onShowToast('Çalışma uğurla silindi.', 'success');
    } catch (err) {
      setShowDeleteConfirm(false);
    }
  };

  // ADD
  const handleAdd = async () => {
    if (!addForm.name.trim()) { setAddError('Ad mütləqdir.'); return; }
    setAddLoading(true);
    setAddError('');
    try {
      let fileUuidName = '';
      let fileName = '';
      if (addFile) {
        const uploaded = await uploadFile(addFile);
        fileUuidName = uploaded.fileUuidName;
        fileName = uploaded.fileName;
      }
      const response = await apiFetch(`${BASE_URL}/lecture-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: addForm.name, description: addForm.description, sector: addForm.sector, fileUuidName, lectureId })
      });
      if (!response.ok) {
        let msg = 'Əlavə edilərkən xəta baş verdi.';
        try { const d = await response.json(); msg = d.message || d.error || msg; } catch (_) {}
        throw new Error(msg);
      }
      const newWork = await response.json();
      // Serverdən gələn obyektə fileName əlavə et
      if (fileName) newWork._fileName = fileName;
      setWorks(prev => [...prev, newWork]);
      setAddForm({ name: '', description: '', sector: '' });
      setAddFile(null);
      setShowAddPanel(false);
      onShowToast('Çalışma uğurla əlavə edildi.', 'success');
    } catch (err) {
      handleApiError({ message: err.message || 'Əlavə edilərkən xəta baş verdi.' });
    } finally {
      setAddLoading(false);
    }
  };

  const handleOpenEdit = () => {
    if (!selectedWork) return;
    setShowAddPanel(false);
    setEditError('');
    setEditFile(null);
    setEditForm({ name: selectedWork.name || '', description: selectedWork.description || '', sector: selectedWork.sector || '', fileUuidName: selectedWork.fileUuidName || '' });
    setShowEditPanel(true);
  };

  // EDIT
  const handleEdit = async () => {
    if (!editForm.name.trim()) { setEditError('Ad mütləqdir.'); return; }
    setEditLoading(true);
    setEditError('');
    try {
      let fileUuidName = editForm.fileUuidName;
      let fileName = selectedWork._fileName || '';
      if (editFile) {
        const uploaded = await uploadFile(editFile);
        fileUuidName = uploaded.fileUuidName;
        fileName = uploaded.fileName;
      }
      const response = await apiFetch(`${BASE_URL}/lecture-tasks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedWork.id, name: editForm.name, description: editForm.description, sector: editForm.sector, fileUuidName, lectureId })
      });
      if (!response.ok) {
        let msg = 'Redaktə edilərkən xəta baş verdi.';
        try { const d = await response.json(); msg = d.message || d.error || msg; } catch (_) {}
        throw new Error(msg);
      }
      // Server yeni fileUuidName qaytarmır — onRefresh ilə təzə UUID alınır
      // Amma fileName lokal saxlanır ki növbəti yükləmədə düzgün ad olsun
      const updatedWork = { ...selectedWork, name: editForm.name, description: editForm.description, sector: editForm.sector, fileUuidName, _fileName: fileName };
      setWorks(prev => prev.map(w => w.id === selectedWork.id ? updatedWork : w));
      setSelectedWork(null);
      setShowEditPanel(false);
      setEditFile(null);
      onShowToast('Çalışma uğurla redaktə olundu.', 'success');
    } catch (err) {
      handleApiError({ message: err.message || 'Redaktə edilərkən xəta baş verdi.' });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDownload = async (workId, e) => {
    e.stopPropagation();
    const work = works.find(w => w.id === workId);
    if (!work?.fileUuidName || downloadingId === workId) return;
    setDownloadingId(workId);
    try {
      const res = await fetch(`${BASE_URL}/files/download-lecture-task/${work.fileUuidName}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = work._fileName || work.fileName || work.fileUuidName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {} finally {
      setDownloadingId(null);
    }
  };

  const handleCancelAdd = () => { setCancelTarget('add'); setShowCancelConfirm(true); };
  const handleCancelEdit = () => { setCancelTarget('edit'); setShowCancelConfirm(true); };
  const confirmCancel = () => {
    if (cancelTarget === 'add') { setShowAddPanel(false); setAddForm({ name: '', description: '', sector: '' }); setAddFile(null); setAddError(''); }
    if (cancelTarget === 'edit') { setShowEditPanel(false); setEditForm({ name: '', description: '', sector: '', fileUuidName: '' }); setEditFile(null); setEditError(''); }
    setShowCancelConfirm(false);
    setCancelTarget(null);
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* ---- BAŞLIQ ---- */}
        <div style={{ background: '#f3f4f6', borderBottom: '1px solid #e5e7eb', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 11, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Çalışmalar</span>
            {works.length > 0 && (
              <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{works.length}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <button disabled={!selectedWork} onClick={handleOpenEdit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: selectedWork ? '#3b82f6' : '#bfdbfe', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontWeight: 600, fontSize: 11, cursor: selectedWork ? 'pointer' : 'not-allowed' }}>Redaktə et</button>
            <button disabled={!selectedWork} onClick={() => setShowDeleteConfirm(true)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: selectedWork ? '#ef4444' : '#fecaca', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontWeight: 600, fontSize: 11, cursor: selectedWork ? 'pointer' : 'not-allowed' }}>Sil</button>
            <button onClick={() => { setShowEditPanel(false); setShowAddPanel(p => !p); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: showAddPanel ? '#15803d' : '#22c55e', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontWeight: 600, fontSize: 11, cursor: 'pointer', transition: 'background 0.15s' }}>
              <svg viewBox="0 0 22 22" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ width: 11, height: 11 }}>
                <path d="M11.75 8C11.75 7.586 11.414 7.25 11 7.25S10.25 7.586 10.25 8v2.25H8c-.414 0-.75.336-.75.75s.336.75.75.75h2.25V14c0 .414.336.75.75.75s.75-.336.75-.75v-2.25H14c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-2.25V8Z"/>
              </svg>
              Yeni çalışma
            </button>
          </div>
        </div>

        {/* ---- ƏLAVƏ ET PANELİ ---- */}
        <div style={{ maxHeight: showAddPanel ? 360 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease', flexShrink: 0 }}>
          <div style={{ background: '#f0fdf4', borderBottom: '2px solid #22c55e', padding: '12px 12px 10px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>+ Yeni Çalışma</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Ad <span style={{ color: '#ef4444' }}>*</span></label>
                <input value={addForm.name} onChange={e => { setAddForm(p => ({ ...p, name: e.target.value })); setAddError(''); }} placeholder="Çalışma adı" style={{ ...inputStyle, borderColor: addError && !addForm.name.trim() ? '#ef4444' : '#e5e7eb' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Sektor</label>
                <select value={addForm.sector} onChange={e => setAddForm(p => ({ ...p, sector: e.target.value }))} style={inputStyle}>
                  <option value="">Seçin</option>
                  <option value="Az">AZ</option>
                  <option value="Rus">RUS</option>
                  <option value="Eng">ENG</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Təsvir <span style={{ color: '#9ca3af', textTransform: 'none', fontWeight: 400 }}>(istəyə bağlı)</span></label>
              <textarea
                value={addForm.description}
                onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Çalışma haqqında ətraflı məlumat..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Fayl <span style={{ color: '#9ca3af', textTransform: 'none', fontWeight: 400 }}>(istəyə bağlı)</span></label>
              <FileSelectInput selectedFile={addFile} onSelect={setAddFile} />
            </div>
            {addError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, padding: '4px 10px', color: '#dc2626', fontSize: 11, marginBottom: 8 }}>{addError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button onClick={handleCancelAdd} disabled={addLoading} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, padding: '4px 12px', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Ləğv et</button>
              <button onClick={handleAdd} disabled={addLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: addLoading ? '#86efac' : '#22c55e', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', fontWeight: 600, fontSize: 11, cursor: addLoading ? 'not-allowed' : 'pointer' }}>
                {addLoading ? <><AppSpinner inline />{addFile ? 'Fayl yüklənir...' : 'Əlavə edilir...'}</> : 'Əlavə et'}
              </button>
            </div>
          </div>
        </div>

        {/* ---- REDAKTƏ PANELİ ---- */}
        <div style={{ maxHeight: showEditPanel ? 400 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease', flexShrink: 0 }}>
          <div style={{ background: '#eff6ff', borderBottom: '2px solid #3b82f6', padding: '12px 12px 10px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Redaktə — {selectedWork?.name || selectedWork?.title}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Ad <span style={{ color: '#ef4444' }}>*</span></label>
                <input value={editForm.name} onChange={e => { setEditForm(p => ({ ...p, name: e.target.value })); setEditError(''); }} placeholder="Çalışma adı" style={{ ...inputStyle, borderColor: editError && !editForm.name.trim() ? '#ef4444' : '#e5e7eb' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Sektor</label>
                <select value={editForm.sector} onChange={e => setEditForm(p => ({ ...p, sector: e.target.value }))} style={inputStyle}>
                  <option value="">Seçin</option>
                  <option value="Az">AZ</option>
                  <option value="Rus">RUS</option>
                  <option value="Eng">ENG</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Təsvir <span style={{ color: '#9ca3af', textTransform: 'none', fontWeight: 400 }}>(istəyə bağlı)</span></label>
              <textarea
                value={editForm.description}
                onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Çalışma haqqında ətraflı məlumat..."
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>
                Fayl
                {editForm.fileUuidName && !editFile && (
                  <span style={{ color: '#15803d', textTransform: 'none', fontWeight: 400, marginLeft: 4 }}>— mövcud fayl var</span>
                )}
                {editFile && (
                  <span style={{ color: '#d97706', textTransform: 'none', fontWeight: 400, marginLeft: 4 }}>— köhnə fayl əvəz olunacaq</span>
                )}
              </label>
              <FileSelectInput selectedFile={editFile} onSelect={setEditFile} />
            </div>
            {editError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, padding: '4px 10px', color: '#dc2626', fontSize: 11, marginBottom: 8 }}>{editError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button onClick={handleCancelEdit} disabled={editLoading} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, padding: '4px 12px', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Ləğv et</button>
              <button onClick={handleEdit} disabled={editLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: editLoading ? '#93c5fd' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', fontWeight: 600, fontSize: 11, cursor: editLoading ? 'not-allowed' : 'pointer' }}>
                {editLoading ? <><AppSpinner inline />{editFile ? 'Fayl yüklənir...' : 'Yadda saxlanılır...'}</> : 'Yadda saxla'}
              </button>
            </div>
          </div>
        </div>

        {/* ---- SİYAHI ---- */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {works.length > 0 ? (
            works.map((work, index) => {
              const isSelected = selectedWork?.id === work.id;
              return (
                <div
                  key={work.id}
                  onClick={(e) => { setSelectedWork(e.ctrlKey && isSelected ? null : work); setShowEditPanel(false); }}
                  style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid #f3f4f6', background: isSelected ? '#eff6ff' : '#fff', cursor: 'pointer', transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{index + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {work.name || work.title || `Çalışma ${index + 1}`}
                      </div>
                      {work.description && (
                        <div style={{ fontSize: 10, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                          {work.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        {work.sector && <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '0px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600, border: '1px solid #bfdbfe' }}>{work.sector}</span>}
                        {work.register && <span style={{ fontSize: 9, color: '#9ca3af' }}>{work.register.split('T')[0]}</span>}
                        {work.creatorUsername && <span style={{ background: '#f5f3ff', color: '#7c3aed', padding: '0px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600, border: '1px solid #ddd6fe' }}>@{work.creatorUsername}</span>}
                      </div>
                    </div>
                    {work.fileUuidName && (
                      <button
                        onClick={e => handleDownload(work.id, e)}
                        disabled={downloadingId === work.id}
                        title="Faylı yüklə"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          background: downloadingId === work.id ? '#e0f2fe' : '#f0f9ff',
                          color: '#0369a1',
                          border: '1px solid #bae6fd', borderRadius: 4,
                          padding: '3px 7px', fontSize: 10, fontWeight: 600,
                          cursor: downloadingId === work.id ? 'not-allowed' : 'pointer', flexShrink: 0
                        }}
                      >
                        {downloadingId === work.id ? (
                          <><AppSpinner inline /> Yüklənir...</>
                        ) : (
                          <>
                            <svg viewBox="0 0 20 20" fill="none" style={{ width: 11, height: 11 }}>
                              <path d="M10 3v10M6 13l4 4 4-4M4 17h12" stroke="#0369a1" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Yüklə
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '50px 16px', color: '#9ca3af', textAlign: 'center' }}>
              <i className="bi bi-pencil-square" style={{ fontSize: 30, marginBottom: 8, display: 'block', color: '#d1d5db' }}></i>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', margin: '0 0 4px' }}>Hələ ki çalışma yoxdur</p>
              <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>Yeni çalışma əlavə etmək üçün yuxarıdakı düyməni istifadə edin</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Silməni Təsdiqlə"
        message={<><strong>{selectedWork?.name || selectedWork?.title}</strong> adlı çalışmanı silmək istədiyinizə əminsiniz?</>}
        confirmText="Bəli, Sil"
      />
      <ConfirmModal
        show={showCancelConfirm}
        onHide={() => { setShowCancelConfirm(false); setCancelTarget(null); }}
        onConfirm={confirmCancel}
        title="Ləğv et"
        message="Daxil edilmiş məlumatlar itəcək. Davam etmək istəyirsiniz?"
        confirmText="Bəli, Ləğv et"
      />
      <ValidationErrorModal show={!!errorModal} onHide={clearError} message={errorModal?.message} validations={errorModal?.validations} />
    </>
  );
};

const labelStyle = {
  display: 'block', fontSize: 10, fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase',
  letterSpacing: '0.04em', marginBottom: 3
};

const inputStyle = {
  width: '100%', border: '1px solid #e5e7eb',
  borderRadius: 5, padding: '5px 8px',
  fontSize: 11, color: '#111827', background: '#fff',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
};

export default LectureWorks;
